//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  getInstagramShopTagCatalogs,
  getInstagramShopTagEligibility,
} from '@apis/instagram-shops/instagramShopsApi';
import { IInstagramShopsCatalog } from '@apis/instagram-shops/instagramShopsApiModel';
import { IInstagramShopsState } from '@instagram-account/redux/instagram-shops/instagramShopsModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IInstagramShopsState = {
  catalogs: [],
  isEligible: false,
  flags: {
    isFetchingEligibility: true,
    isFetchingCatalogs: false,
  },
  errors: {},
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramShops,
  initialState,
  reducers: {
    requestInstagramShopsEligibility(state) {
      state.flags.isFetchingEligibility = true;
      state.errors.fetchEligibilityError = undefined;
    },
    receiveInstagramShopsEligibilitySucceeded(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.flags.isFetchingEligibility = false;
      state.isEligible = action.payload;
    },
    receiveInstagramShopsEligibilityFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isFetchingEligibility = false;
      state.errors.fetchEligibilityError = action.payload;
    },
    requestInstagramShopsCatalogs(state) {
      state.flags.isFetchingCatalogs = true;
      state.errors.fetchCatalogsError = undefined;
    },
    receiveInstagramShopsCatalogsSucceeded(
      state,
      action: PayloadAction<IInstagramShopsCatalog[]>,
    ) {
      state.flags.isFetchingCatalogs = false;
      state.catalogs = action.payload;
    },
    receiveInstagramShopsCatalogsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingCatalogs = false;
      state.errors.fetchCatalogsError = action.payload;
    },
  },
});

export const fetchInstagramShopsEligibility = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestInstagramShopsEligibility());
    try {
      const isEligible = await getInstagramShopTagEligibility();
      dispatch(actions.receiveInstagramShopsEligibilitySucceeded(isEligible));
    } catch (e) {
      const message =
        getRequestErrorMessage(e) ??
        'Failed to get Instagram Shops eligibility status';
      dispatch(actions.receiveInstagramShopsEligibilityFailed(message));
    }
  },
);

export const fetchInstagramShopsCatalogs = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestInstagramShopsCatalogs());

    try {
      const catalogs = await getInstagramShopTagCatalogs();
      dispatch(actions.receiveInstagramShopsCatalogsSucceeded(catalogs));
    } catch (e) {
      const message =
        getRequestErrorMessage(e) ??
        'Failed to retrieve Instagram Shops catalogs';
      dispatch(actions.receiveInstagramShopsCatalogsFailed(message));
    }
  },
);

export default reducer;
